import moment from "moment";

type DateInput = Date | string | null | undefined;

const toMoment = (value: DateInput) => {
  if (!value) {
    return null;
  }

  const parsed = moment(value);

  return parsed.isValid() ? parsed : null;
};

/**
 * Dates a payment for an invoice may have (Roadmap 1809): from the invoice
 * date up to today. If the invoice is dated in the future, its own date is
 * still allowed so the window never offers an empty range.
 */
export const paymentDateBounds = (
  invoiceDate: DateInput,
  today: Date = new Date(),
): { minDate?: Date; maxDate: Date } => {
  const invoiceDay = toMoment(invoiceDate)?.startOf("day");
  const endOfToday = moment(today).endOf("day");

  if (!invoiceDay) {
    return { maxDate: endOfToday.toDate() };
  }

  const maxDate = invoiceDay.isAfter(endOfToday)
    ? invoiceDay.clone().endOf("day")
    : endOfToday;

  return { minDate: invoiceDay.toDate(), maxDate: maxDate.toDate() };
};

/** True when the payment date is inside {@link paymentDateBounds}. */
export const isPaymentDateAllowed = (
  paymentDate: DateInput,
  bounds: { minDate?: Date; maxDate: Date },
) => {
  const date = toMoment(paymentDate);

  if (!date) {
    return false;
  }

  if (bounds.minDate && date.isBefore(bounds.minDate, "day")) {
    return false;
  }

  return !date.isAfter(bounds.maxDate, "day");
};

/** True when the due date is a day before the invoice date (Roadmap 1812). */
export const isDueDateBeforeInvoiceDate = (
  invoiceDate: DateInput,
  dueDate: DateInput,
) => {
  const invoiceDay = toMoment(invoiceDate);
  const dueDay = toMoment(dueDate);

  if (!invoiceDay || !dueDay) {
    return false;
  }

  return dueDay.isBefore(invoiceDay, "day");
};

export const PAYMENT_DATE_ERROR =
  "The payment date must be between the invoice date and today.";

export const DUE_DATE_ERROR = "The due date can't be before the invoice date.";

export const FUTURE_PAYMENT_DATE_ERROR =
  "The payment date can't be in the future.";

/**
 * Billing > Payments > New payment (QA C2, 2026-10-22): the same rule as
 * "Record a payment", for every invoice that gets an amount. The date may not
 * be after today, nor before the newest of those invoices' dates. Returns the
 * message to show, or null when the date is fine.
 */
export const newPaymentDateError = (
  paymentDate: DateInput,
  paidInvoiceDates: DateInput[],
  today: Date = new Date(),
): string | null => {
  const date = toMoment(paymentDate);
  if (!date) return PAYMENT_DATE_ERROR;
  if (date.isAfter(moment(today).endOf("day"), "day")) {
    return paidInvoiceDates.some((d) => toMoment(d))
      ? PAYMENT_DATE_ERROR
      : FUTURE_PAYMENT_DATE_ERROR;
  }
  const latest = paidInvoiceDates
    .map(toMoment)
    .filter((d): d is moment.Moment => !!d)
    .sort((a, b) => b.valueOf() - a.valueOf())[0];
  if (!latest) return null;
  return isPaymentDateAllowed(
    date.toDate(),
    paymentDateBounds(latest.toDate(), today),
  )
    ? null
    : PAYMENT_DATE_ERROR;
};

/**
 * Billing dates in the app's format (QA batch 17, UI-005): "Oct 22, 2026" like
 * New work, instead of 2026/10/22 or 10/22/2026. Date boxes use
 * BILLING_DATE_PICKER_FORMAT (date-fns) and still accept 2026/10/22 typed.
 */
export const BILLING_DATE_PICKER_FORMAT = "MMM dd, yyyy";

export const formatBillingDate = (value: DateInput): string =>
  toMoment(value)?.format("MMM DD, YYYY") ?? "";
